import { hashKey, useInfiniteQuery } from '@tanstack/react-query'
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import { useEffect, useMemo } from 'react'
import type { AppRouter } from '@/api/_trpc/router'

type Inputs = inferRouterInputs<AppRouter>['admin']['coachingV2']['curriculum']
type Outputs =
  inferRouterOutputs<AppRouter>['admin']['coachingV2']['curriculum']

export type BrowsePage = Outputs['browseLearningItems']

/**
 * The server-evaluated slice of the browse query: which learning-item kinds
 * (the type pills) and which filter tree. Everything else about paging is
 * this module's business.
 */
export type BrowseFiltersInput = Pick<
  Inputs['browseLearningItems'],
  'kinds' | 'filters'
>

/** How the hook reaches the server. Injected so tests need no tRPC. */
export type BrowsePageFetcher = (
  input: Inputs['browseLearningItems'],
) => Promise<BrowsePage>

/** The zod input caps `limit` at 100; this is that ceiling, not a tuning knob. */
export const BROWSE_PAGE_SIZE = 100

/**
 * Hard stop on the drain loop. `getNextPageParam` trusts the server's
 * `nextCursor`, so a bug that never returns null would otherwise spin
 * requests forever. 40 pages is ~6x the published corpus: unreachable in
 * normal operation, and a bounded failure when it is not.
 */
export const MAX_BROWSE_PAGES = 40

/**
 * Refetching an infinite query re-runs every page in sequence, so the global
 * 30s staleTime would fire seven requests each time a coach returns to the
 * tab. Browse rows are catalog-level and change only on an admin edit.
 */
export const BROWSE_STALE_MS = 5 * 60_000
export const BROWSE_GC_MS = 15 * 60_000

/**
 * Canonical form so semantically equal filter slices produce identical query
 * keys: empty kinds/filters collapse to `undefined`, which query-key hashing
 * drops entirely. Without this the slice's mount-time sync (`filters: []`)
 * would hash differently from the initial `{ kinds }` state and fire a
 * redundant first request.
 */
export function normalizeBrowseFilters(
  input: BrowseFiltersInput,
): BrowseFiltersInput {
  return {
    kinds: input.kinds && input.kinds.length > 0 ? input.kinds : undefined,
    filters:
      input.filters && input.filters.length > 0 ? input.filters : undefined,
  }
}

export function browseQueryKey(
  filters: BrowseFiltersInput,
): readonly unknown[] {
  return ['coaching', 'curriculum', 'browse-corpus', filters]
}

export interface BrowseDrainState {
  hasNextPage: boolean
  isFetching: boolean
  isFetchingNextPage: boolean
  isPlaceholderData: boolean
  isError: boolean
  pageCount: number
}

/**
 * Whether the drain effect should pull one more page. Pure so the loop's
 * stopping conditions are testable without a DOM or a server: the effect
 * itself is a one-line shell over this.
 */
export function shouldFetchNextBrowsePage(state: BrowseDrainState): boolean {
  if (!state.hasNextPage) return false
  // A failed page must not be retried on every render.
  if (state.isError) return false
  // Placeholder pages belong to the previous filter; their cursor does not.
  if (state.isPlaceholderData) return false
  if (state.isFetching || state.isFetchingNextPage) return false
  return state.pageCount < MAX_BROWSE_PAGES
}

/**
 * Identity of the drain step to run, or `null` for "stay put" — the drain
 * effect's only dependency. Both halves of the identity are load-bearing:
 *
 * - The page count, because when a page lands React Query often resolves the
 *   request before it publishes the in-flight render, so `wantsMore` goes
 *   true → true with no false in between. An effect keyed on `wantsMore`
 *   alone therefore fires exactly once and the drain stalls one page in.
 * - The query key, for the mirror-image reason: two cached slices abandoned
 *   mid-drain at the same page count would otherwise compare equal across a
 *   filter switch, and the slice switched back to would park with a live
 *   cursor and no re-run.
 *
 * Pure, and a derived string rather than a raw page count, so the effect's
 * dep array stays a single value that biome's `useExhaustiveDependencies`
 * accepts.
 */
export function browseDrainToken(
  filters: BrowseFiltersInput,
  pageCount: number,
  wantsMore: boolean,
): string | null {
  if (!wantsMore) return null
  return `${hashKey(browseQueryKey(filters))}:${pageCount}`
}

/**
 * What the hook hands a renderer. The two busy flags are independent
 * questions, not a state machine: during a filter change `isStreaming` and
 * `isRefreshing` are BOTH true, and neither excludes the other — the rows on
 * screen are stale (`isRefreshing`) *and* the new slice has not finished
 * arriving (`isStreaming`). A consumer that renders them as mutually
 * exclusive will silently drop one of them.
 */
export interface BrowseCorpusState {
  /** Every page loaded so far, merged into one flat corpus. */
  data: BrowsePage['items'] | undefined
  isLoading: boolean
  /**
   * True while a filter change refetches but the previous corpus is still on
   * screen via placeholderData. The table dims and marks itself busy so the
   * briefly-stale rows do not read as authoritative. Deliberately false while
   * merely streaming: a filling table is usable, not stale.
   */
  isRefreshing: boolean
  /**
   * "The rows on screen are not the whole answer." True on first paint, while
   * the previous slice's placeholder rows stand in for a new one, and across
   * every gap in the drain. So `!isStreaming` is a sound test for "every row
   * that is coming has arrived"; `isLoading` distinguishes the first-paint
   * case for callers that need to. A same-key background refetch of an
   * already-drained corpus is deliberately NOT streaming: no row is going to
   * move, and the only consumer renders this as "loading the rest…".
   */
  isStreaming: boolean
  /**
   * The drain stopped with rows still unfetched — a failed page or the paging
   * cap. Sourced from `hasNextPage` rather than from `error`, because a failed
   * *background refetch* of an already-drained corpus also sets `error` while
   * leaving every row in place; calling that "incomplete" would warn about a
   * library that is in fact whole.
   */
  isTruncated: boolean
  error: unknown
  refetch: () => Promise<unknown>
}

/**
 * Loads the whole browse corpus for one filter slice.
 *
 * Pages live in the app's QueryClient rather than component state, so they
 * survive the Curriculum tab unmounting — that is the point. The query key is
 * the filter slice, which makes it the invalidation rule too: a filter change
 * is a different key, so stale rows cannot merge into fresh ones. When §8 adds
 * per-client seen data to the input, it joins the key automatically.
 *
 * `fetchPage` is injected rather than imported so this hook can be tested
 * against a real QueryClient without standing up tRPC.
 */
export function useBrowseCorpus(
  filters: BrowseFiltersInput,
  fetchPage: BrowsePageFetcher,
): BrowseCorpusState {
  const query = useInfiniteQuery({
    queryKey: browseQueryKey(filters),
    queryFn: ({ pageParam }) =>
      fetchPage({ limit: BROWSE_PAGE_SIZE, afterId: pageParam, ...filters }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    // Keep the previous corpus on screen while a filter change refetches, so
    // the library never collapses to a loading state mid-edit.
    placeholderData: (previous) => previous,
    staleTime: BROWSE_STALE_MS,
    gcTime: BROWSE_GC_MS,
    // Same rationale as the staleTime above, and for the same reason it has to
    // be said twice: a focus refetch re-runs every page serially, so an
    // alt-tab back into the tab would fire the whole drain again. Browse rows
    // change only on an admin edit, so that cost is never repaid.
    refetchOnWindowFocus: false,
  })

  const pageCount = query.data?.pages.length ?? 0
  const wantsMore = shouldFetchNextBrowsePage({
    hasNextPage: query.hasNextPage,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    pageCount,
  })

  const drainStep = browseDrainToken(filters, pageCount, wantsMore)

  const { fetchNextPage } = query
  useEffect(() => {
    if (drainStep === null) return
    void fetchNextPage()
  }, [drainStep, fetchNextPage])

  useEffect(() => {
    if (pageCount < MAX_BROWSE_PAGES || !query.hasNextPage) return
    // Reaching this means the server kept offering a cursor past a bound set
    // ~6x above the real corpus. The table is showing a truncated library.
    console.error(
      `[curriculum] browse drain stopped at the ${MAX_BROWSE_PAGES}-page cap with a cursor still pending; the library is truncated.`,
    )
  }, [pageCount, query.hasNextPage])

  const data = useMemo<BrowsePage['items'] | undefined>(() => {
    const pages = query.data?.pages
    if (!pages) return undefined
    return pages.flatMap((entry) => entry.items)
  }, [query.data])

  // `isLoading` covers the opening request, before which nothing has arrived
  // at all; `isPlaceholderData` covers a filter change, whose rows belong to
  // the previous slice; `wantsMore` covers the gap between two drain steps and
  // `isFetchingNextPage` covers one in flight.
  //
  // The last clause covers a whole-query refetch — `refetchOnMount` over a
  // slice that was abandoned mid-drain and is still cached. That refetch sets
  // neither `isFetchingNextPage` nor `wantsMore` (the drain bails while any
  // fetch is in flight), so without it the hook would report a parked,
  // still-filling corpus as settled and `isTruncated` would paint the amber
  // truncation warning for the length of the refetch. `hasNextPage` is what
  // keeps this from undoing the point of the clause above it: an already-drained
  // corpus has no next page, so its background refetch is still not streaming.
  const isStreaming =
    query.isLoading ||
    query.isPlaceholderData ||
    wantsMore ||
    query.isFetchingNextPage ||
    (query.isFetching && query.hasNextPage)

  return {
    data,
    isLoading: query.isLoading,
    isRefreshing: query.isPlaceholderData,
    isStreaming,
    isTruncated: data !== undefined && !isStreaming && query.hasNextPage,
    error: query.error,
    refetch: query.refetch,
  }
}
